import { InjectionToken } from '@angular/core';
import { ConfigConstants } from '../config/config-constants';
import { InitializableInterface } from './initializable.interface';

/**
 * Public contract shared by the config services (legacy ConfigService and the NgRx-based
 * ConfigNgrxService). Covers every public method of both implementations so either can be
 * provided behind this interface.
 */
export interface ConfigInterface extends InitializableInterface {
    getMaterialErrorCodeCountMax(): number;
    getCseEmailFax(): string;
    getShowRefreshXMLButton(): boolean;
    getDisplayCustomerContact(): boolean;
    getShowLanguageSelectionDropdown(): boolean;
    getEscalationEmail(): string;
    getInstructionsUrl(): string;
    getShowDocumentationLink(): boolean;
    getUserFrontId(): string;
    getMaxInterimsCount(): number;
    getComplaintNotificationType(notificationType: ConfigConstants.NotificationTypes): boolean;
    getExpenseCurrency(): string;
    getCseCountry(): string;
    getShowInfoToCCC(): boolean;
    getLegalCheckIEC62353Text(): string;
    getLegalCheckIEC62353Code(): string;
    getLegalCheckPCResistanceText(): string;
    getLegalCheckPCResistanceCode(): string;
    getLegalCheckDeviceLeakageText(): string;
    getLegalCheckDeviceLeakageCode(): string;
    getLegalCheckXRayText(): string;
    getLegalCheckXRayCode(): string;
    getLegalCheckRadioProtectionText(): string;
    getLegalCheckRadioProtectionCode(): string;
    getLegalCheckInstallationCheckText(): string;
    getLegalCheckInstallationCheckCode(): string;
    getLegalCheckStatusCheckText(): string;
    getLegalCheckStatusCheckCode(): string;
    getLegalCheckConsistencyCheckText(): string;
    getLegalCheckConsistencyCheckCode(): string;
    getLegalCheckPartiallyCheckedText(): string;
    getLegalCheckPartiallyCheckedCode(): string;
    getLegalCheckAcceptanceTestCode(): string;
    getLegalCheckPartialAcceptanceTestCode(): string;
    getLegalCheckHealthCheckCode(): string;
    getLegalCheckConsistencyTestCode(): string;
    getLegalCheckNEN1010Text(): string;
    getLegalCheckNEN1010Code(): string;
    getLegalCheckK3RoomText(): string;
    getLegalCheckK3RoomCode(): string;
    getLegalCheckMeasurementPerformedText(): string;
    getLegalCheckMeasurementPerformedCode(): string;
    getLegalCheckInsulationResistanceText(): string;
    getLegalCheckInsulationResistanceCode(): string;
    getLegalCheckMeasurementNotNeededText(): string;
    getLegalCheckMeasurementNotNeededCode(): string;
    getEowSowBreakHours(): number;
    getEowFlexibleHours(): number;
    showEquipment(taskGroup: string): boolean;
    showMaterialConsumption(taskGroup: string): boolean;
    showMeasurementTools(taskGroup: string): boolean;
    showParticipantList(taskGroup: string): boolean;
    showFollowup(taskGroup: string): boolean;
    showFollowupLongText(): boolean;
    showFollowupAdditionalInfo(): boolean;
    showMeasuringPoints(taskGroup: string): boolean;
    getFollowupDefaultAdditionalTime(): number;
    getFollowupMinAdditionalTime(): number;
    getFollowupAdditionalTimeStep(): number;
    getAppsTaskCode(): string;
    getStatusAfterFixValueForQCFlag(): string;
    getEtaLimitMinutes(): number;
    getMaterialHistoryLimit(): number;
    getNotificationsHistoryDaysLimit(): number;
    getCorpInfoHQ1(): string;
    getCorpInfoHQ2(): string;
    getCorpInfoHQ3(): string;
    getCorpInfoHQ4(): string;
    getCorpInfoHQ5(): string;
    getCorpInfoHQ5Link(): string;
    getCorpInfoHQTel(): string;
    getCorpInfoSupportHours(): string;
    getCorpInfoSupportEmail(): string;
    getCorpInfoSupportLink(): string;
    getCorpInfoSupportCreateTicketLink(): string;
    getCorpInfoLegalInfo1(): string;
    getCorpInfoLegalInfo2(): string;
    getCorpInfoLegalInfo3(): string;
    getCorpInfoLegalInfo4(): string;
    getCorpInfoLegalInfo5(): string;
    getCorpInfoLegalInfo6(): string;
    getCorpInfoLegalInfo7(): string;
    getCorpInfoLegalInfoPolicy(): string;
    getCorpInfoLegalInfoPolicyLink(): string;
    getCorpInfoLegalInfoTOU(): string;
    getCorpInfoLegalInfoTOULink(): string;
    getCorpInfoLegalPolicyMonth(): string;
    getGlobalStorageLocation(): string[];
    getGlobalStorageLocationMatPricing(): string[];
    getGlobalStorageLocationMatRequest(): string;
    getCIDefaultText(): string;
    getPreviousDaysTimesheetLimit(): number;
    getMaxAllowedFutureTime(): number;
    getDefaultFollowUpTimeP58(): string;
    getUseCustomisedForms(): boolean;
    getLegalCheckVDE701702Text(): string;
    getLegalCheckVDE701702Code(): string;
    getLegalCheckVDEPCResistanceText(): string;
    getLegalCheckVDEPCResistanceCode(): string;
    getLegalCheckVDEDeviceLeakageText(): string;
    getLegalCheckVDEDeviceLeakageCode(): string;
    getLegalCheckVDEInsulationResistanceText(): string;
    getLegalCheckVDEInsulationResistanceCode(): string;
    getUseNoCustomErrorCode(): boolean;
    getUseAddNewMaterialReturn(): boolean;
    getReportTextLineLength(): number;
    getReportTextMaxLines(): number;
    getMaxRecipientEntries(): number;
    getSelectDefaultFollowupReason(): boolean;
    getEnableSafetyAssessmentForm(): boolean;
    getBootstockReplenishEnabled(): boolean;
    getSafetyAssessmentForm(): string;
    getEnablePaceFormsCrossDeviceSync(): boolean;
    getDisplayAdditionalMaterialRequestInformation(): boolean;
    getAddMaterialCurrencyCode(): string;
    getAddMaterialSelectBillableDefault(): boolean;
    getAddMaterialBillingIndicator(): string;
    getAddMaterialBillingChangeable(): boolean;
    getShowCommercialInfo(): boolean;
    getCSMLTrackerLink(): string;
    getAddDeliveryAddressTaxi(): boolean;
    getDisablePaceForms(): boolean;
    getDisplayNonProductiveHours(): boolean;
    getDisplayAdditionalTravelBack(): boolean;
    getDisplayTravelBackSummary(): boolean;
    getDisplayAdditionalTravelInfo(): boolean;
    getDisplayMeansOfTransport(): boolean;
    getDisplayAdditionalMaterialReturnInformation(): boolean;
    getFormatMultilineTextPattern(): string;
    getFormatInternalInfoMultilineTextPattern(): string;
    getDivisionsWithAdditionalLegalText(division: string): boolean;
    getDivisionsWithIEC62353(division: string): boolean;
    getDivisionsWithVDE701702(division: string): boolean;
    getParticipantCopySupportByNotificationType(notificationType: ConfigConstants.NotificationTypes): string[];
    getFormatMomentInUTC(): boolean;
    getDisplayTimesheetAddress(): boolean;
    getTravelBackStartEditable(): boolean;
    getTravelBackEndEditable(): boolean;
    getTimesheetEndTimeLimit(): string;
    getDisplayTimesheetAddressOptions(): boolean;
    getTimesheetAddressHome(): string;
    getTimesheetAddressOffice(): string;
    getMatPrioritiesForDOP(): string;
    getMatPrioritiesForCustomer(): string;
    getDisplayMaterialQuote(): boolean;
    getDisplayCustomerSignatureInForm(): boolean;
    getTimesheetPauseCodes(taskType: string): boolean;
    getSparePartsCatalogLink(): string;
    getDisableQuoteForNotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean;
    getEscalateNotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean;
    getDefaultReasonCodeForCentralScrap(): string;
    getDisableSpecialTransportAWBfield(): boolean;
    getSpecialDangerousGoodsInd(dangerousGoodInd: string): boolean;
    getDangerousGoodsIndicator(dangerousGoodInd: string): boolean;
    getFollowUpDefaultDays(): number;
    getDisplayEtaAlert(): boolean;
    getEtaAlertTimerLimit(): number;
    getTextClausePattern(): string;
    getShowMobileNumberForParticipants(): boolean;
    getShowCalibrationDate(): boolean;
    getUseCustomTimeInput(): boolean;
    getShowCustomerInformationOnSummary(): boolean;
    getFormatMultilineTextOnBlur(): boolean;
    getShowFormCustomerEmailData(): boolean;
    getUseCustomDurationStepper(): boolean;
    getPrefillTroubleText(): boolean;
    getEnableJobOnFly(): boolean;
    getJobOnFlyDefaultDuration(): number;
    getDisplayMaterialPricing(): boolean;
    getActivateCosima(): boolean;
    getDisplayBatchNumber(): boolean;
    getDisplayWeeklyWorkTime(): boolean;
    getEnableStandbyTravel(): boolean;
    getIsTravelBillable(orderType: string): boolean;
    getDisplayCentralScrap(): boolean;
    getDisplayCentralScrapForDangerousGoods(): boolean;
    getSmartSensorTracker(): string[];
    getSmartSensorTrackerUrl(): string;
    getMatSerTypeaheadToggle(): boolean;
    getMatLTextDisplayLines(): string[];
    getDisplayAdditionalTravelCost(): boolean;
    getAdditionalTravelCostText(): string;
    getMaxWeeklyWorkingHours(): number;
    getDisplayCseSignature(): boolean;
    getDisplayBootStockIndicator(): boolean;
    getEnableBootstockScrap(): boolean;
    getAllowNotificationPause(): boolean;
    getDisplayTrendCodeInFollowup(): boolean;
    getShowMeasurementToolsDetails(): boolean;
    getHideParticipantFormSubmission(): boolean;
    getShowDataMediumInEquipment(): boolean;
    getAnaLink(): string;
    getShowIconForMaterialDelivery(): boolean;
    getTeamplayFleetLink(): string;
    getComplaintsQuestionnaireLink(appID?: string): string;
    getSparePartCatalogMatLink(): string;
    getEnableTimesheetEmail(): boolean;
    isMaterialLiBatteryStandAlone(unNumber?: string): boolean;
    isMaterialLiBatteryInDevice(unNumber?: string): boolean;
    isTimesheetDisabled(): boolean;
    getDataManagementEmail(): string;
    getDataManagementEmailInvitro(): string;
    getShowFleetLinkInEquipmentHistory(): boolean;
    getShowStartImmediately(): boolean;
    getShowPlaceHolderForReportText(): boolean;
    getShowPlaceHolderForEscalationText(): boolean;
    getMaxWorkingHoursInTimesheet(): number;
    getHideComplaintsForUserStatusType(userStatusType: ConfigConstants.USER_STATUS): boolean;
    getBackendName(): string;
    isMaterialOrderPriorityForSplit(materialOrderPriority: string): boolean;
    isMaterialOrderUnitNonSplittable(unit: string): boolean;
    getEnableMatOrderCheckOnlineOnly(): boolean;
    getDocboxDoctypes(): Array<{ key: string; value: string }>;
    getDocBoxInstallationDocType(): string;
    getDocBoxEnableReportTypeSelection(): boolean;
    getUserCountryFI(): string;
    getDisclaimerForChinaFI(): string;
    getEmojisRegex(): string;
    getLegal3551NotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean;
    getLegal3551DetachPowerCordText(): string;
    getLegal3551PermanentConnectedText(): string;
    getLegal3551NonDetachPowerCordText(): string;
    getLegal3551DetachPowerCordCode(): string;
    getLegal3551PermanentConnectedCode(): string;
    getLegal3551NonDetachPowerCordCode(): string;
    getLegalVerificationNotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean;
    getDivisionsWithVerificationLegalCheck(division: string): boolean;
    getLegalVerifiedSecurityDeviceText(): string;
    getLegalVerifiedSecurityDeviceCode(): string;
    getLegalResumptionAfterUserValidationText(): string;
    getLegalResumptionAfterUserValidationCode(): string;
    getLegalCheckHandoverSafetyInformationText(): string;
    getLegalCheckHandoverUserDocumentationText(): string;
    getLegalCheckHandoverSafetyInformationCode(): string;
    getLegalCheckHandoverUserDocumentationCode(): string;
    getDisplayConfirmSignatures(): boolean;
    getEnableRemoteWork(): boolean;
    getShowRemoteStatusChangeWarning(): boolean;
    getDisplayDocBox(): boolean;
    getShowUploadToDocBoxForNotificationType(notificationType: ConfigConstants.NotificationTypes): boolean;
    getUploadToDocBoxMandatoryForNotificationType(notificationType: ConfigConstants.NotificationTypes): boolean;
    getUploadToDocBoxStatusMandatory(): boolean;
    getUploadToDocBoxAllowedMimeTypes(): string[];
    getMatQuoteDistanceUnit(): string;
    getEnableMatQuoteDistance(): boolean;
    getEnableHideTimesheetAddress(): boolean;
    getHideTimesheetPersonnelSubArea(): string;
    getShowTpfReportVisibility(): boolean;
    getMandatoryTpfReportVisibility(): boolean;
    getDefaultTpfReportVisibility(): number;
    getEquipmentErrorCodeRemoteWork(): string;
    getDisplayConfirmationDistance(): boolean;
    getDisplayConfirmationExpenses(): boolean;
    getDowntimeThresholdInMinutes(): number;
    getEnableIndividualDocBoxDownload(): boolean;
    getPlaceHolderForCustomerIdentification(): string;
    getShowDataMaintenanceEnhancement(): boolean;
    getDisplayServicePartQuestionnaire(): boolean;
    getMaterialOrderTextInfoLineLength(): number;
    getMaterialOrderTextInfoMaxLines(): number;
    getMaterialOrderTextInfoPattern(): string;
    getCSMLInvivoPhoneNumber(): string;
    getCSMLInvitroPhoneNumber(): string;
    getEnableTextInfoValidation(): boolean;
    getByBoxKonnectLink(): string;
    getCBDocLink(): string;
    getSeSoLink(): string;
    getSeSoDivisions(): string[];
    getEnableMatReturnWorkAround(): boolean;
    getDisplayAddMaterialButton(): boolean;
    getEnableBillingIndicator(): boolean;
    getDisplayCustomerPrice(): boolean;
    getEnableManualIndividualization(): boolean;
    getEnableDocumentFeedbackButton(): boolean;
    getEnableFormsHistoricalValues(): boolean;
    getMeasurementToolsDefaultSettingsAPPS(): boolean;
    getHideRecoveryTimeForNotificationTypes(
        notificationType: ConfigConstants.NotificationTypes,
        notificationCategory: string[],
    ): boolean;
    getMaterialOrderDeliveryOptions(): string;
    getMaterialReturnAddressOptions(): string;
    getShowFCCForNotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean;
    getBKInfoTextForBillingIndicatorsNotReceived(): string;
    getShowCyberSecurityQuestion(): boolean;
    getHideAbortAllFormsButton(): boolean;
    getEnableAbortFormInList(): boolean;
    getMatRequestSubAddressCharLimit(): number;
    getMatRequestContactCharLimit(): number;
    getCommercialInfMandatoryForNonBill(): boolean;
    getEnableTextInfoForDop(): boolean;
    getHideSubmitButtonForForms(): boolean;
    getHideAuthorsFormsButtons(): boolean;
    getShowBootOtherStockWarning(taskGroup: string): boolean;
    getLegalCheckDosageCode(): string;
    getLegalCheckDosageText(): string;
    getEnableStatesOfUSADropdown(): boolean;
    getDeliveryOptionsEnabled(): boolean;
    getDeliveryApprovalMinimumLength(): number;
    isCriticalityApplicableOnNotifType(notificationType: ConfigConstants.NotificationTypes): boolean;
    isCustomerDamageEnabled(): boolean;
    isFocReplacementEnabled(): boolean;
    getEnabledAfterHoursCallOut(): boolean;
    getEnableOtherTasks(): boolean;
    getOtherTasksOverlapUpcomingNoti(): boolean;
    getDisplaySideMenuDocBoxFlag(): boolean;
    getReportAnIssueAllowedMimeTypes(): string[];
    getParticipantCreditTypeSelectorEnabled(): boolean;
    getEnableDurationChange(): boolean;
    getEnableDeliveryCutoffTime(): boolean;
    getDocMissingProtocolsEnabled(): boolean;
    getEnableFDA2579Form(): boolean;
    isSourceReplacementEnabled(): boolean;
    getCentricarePCHEnabled(): boolean;
    getCentricarePCHWarningTime(): number;
    getRadioactiveSurveyEnabled(): boolean;
    getRadioactiveSurveyDivisions(division: string): boolean;
    getRadioactiveSurveyToolCalibration(): number | undefined;
    getRadioactiveSurveyRegion(region: string): boolean;
    getPickupRFIDEnabled(): boolean;
    getEnableAWBPackaging(): boolean;
    getEnableFedexAwbPackagingScanning(): boolean;
    getEnableInstallationPaperwork(): boolean;
    getIseCseMetricFormPowerApp(): string;
    getEnableCommercialInfoMandatory(): boolean;
    getShowRemoteWork(): boolean;
    getShowStartImmediatelyFollowUp(): boolean;
    getEnablePhysicalInventory(): boolean;
    getEarlyTravelHours(): number;
    getRentalToolAllowWithNoConsumption(): boolean;
    getEnableBootstockReturn(): boolean;
    getAppBookingForPartners(): boolean;
    getAppBookingEnabled(): boolean;
    getAppBookingMinDate(): number;
    getAppBookingMaxDate(): number;
    getAppBookingMaxDuration(): number;
    getAppBookingShowDedicated(): boolean;
    getAppBookingDefaultDedicated(): boolean;
    getAppBookingITCS(): string;
    getEnableForceCheckin(): boolean;
    getEnableMeasurementToolsForSCRM(): boolean;
    getEnableUnlimitedHandover(): boolean;
    getEnableSmartlocker(): boolean;
    getSmartlockerMaterialIdentifier(): string;
    getDateTimeFormatPattern(): string;
    getUseCsmlUrlWithParam(): boolean;
}

/**
 * DI token for {@link ConfigInterface}. Lets the app choose which implementation to provide
 * (legacy ConfigService or the NgRx-based ConfigService in config.ngrx.service.ts) without
 * consumers depending on a concrete class.
 */
export const ConfigInstanceToken = new InjectionToken<ConfigInterface>('ConfigInterface');

export type ConfigParticipantCopySupportByNotificationType = {
    [key in ConfigConstants.NotificationTypes]?: Array<'832' | '834' | '837' | '336'>;
};
